import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Download, Trash2, Loader2, Save, Plus, X } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MonthPicker } from "@/components/MonthPicker";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendChart } from "@/components/TrendChart";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { METRIC_DEFS } from "@/lib/metrics";
import { formatMetric, formatPeriodLabel } from "@/lib/format";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Metrics() {
  const { company } = useAuth();
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ period: currentMonth() });
  const [customDefs, setCustomDefs] = useState([]);
  const [newDef, setNewDef] = useState({ name: "", unit: "number" });
  const [chartKpi, setChartKpi] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    if (!company) return;
    setLoading(true);
    try {
      const [m, d] = await Promise.all([
        api.get(`/companies/${company.id}/metrics`),
        api.get(`/companies/${company.id}/custom-metric-defs`).catch(() => ({ data: [] })),
      ]);
      setMetrics(m.data);
      setCustomDefs(d.data);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveManual = async (e) => {
    e.preventDefault();
    if (!form.period) return toast.error("Please choose a period");
    setSaving(true);
    try {
      const payload = { period: form.period };
      METRIC_DEFS.forEach((d) => {
        const v = form[d.key];
        payload[d.key] = v === "" || v === undefined ? null : Number(v);
      });
      payload.custom = customDefs
        .map((d) => {
          const v = form[`c_${d.key}`];
          return v === "" || v === undefined ? null : { key: d.key, name: d.name, unit: d.unit, value: Number(v) };
        })
        .filter(Boolean);
      await api.post(`/companies/${company.id}/metrics`, payload);
      toast.success(`Metrics saved for ${formatPeriodLabel(form.period)}`);
      setForm({ period: currentMonth() });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save metrics");
    } finally { setSaving(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/companies/${company.id}/metrics/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} period(s)`);
      if (data.errors?.length) toast.warning(`${data.errors.length} row(s) skipped`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(`/companies/${company.id}/metrics/template`, { responseType: "text" });
      const blob = new Blob([data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "metrics_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/companies/${company.id}/metrics/${id}`); load(); toast.success("Metric period deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const addDef = async () => {
    if (!newDef.name.trim()) return;
    try {
      await api.post(`/companies/${company.id}/custom-metric-defs`, newDef);
      toast.success(`Custom KPI "${newDef.name}" added`);
      setNewDef({ name: "", unit: "number" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add KPI"); }
  };

  const removeDef = async (key) => {
    try { await api.delete(`/companies/${company.id}/custom-metric-defs/${key}`); load(); }
    catch { toast.error("Could not remove KPI"); }
  };

  const customValue = (m, key) => (m.custom || []).find((c) => c.key === key)?.value;

  return (
    <AppShell title="Metrics" subtitle="Track your KPIs over time">
      {!loading && metrics.length === 0 && (
        <Card className="mb-6 rounded-xl border border-brand/30 bg-brand/5 p-5" data-testid="metrics-teaching">
          <h2 className="h-display text-base font-semibold text-foreground">Start with one month</h2>
          <p className="mt-1 text-sm text-muted-foreground">Metrics power your health dashboard, runway estimate, anomaly alerts, and the AI update draft. Any field can stay blank.</p>
          <ol className="mt-4 grid gap-2 sm:grid-cols-3">
            {["Pick the period and enter revenue, cash, and burn below", "Or upload a CSV or Excel file using the template", "Come back each month; TenX shows the change automatically"].map((step, i) => (
              <li key={step} className="flex items-start gap-3 rounded-lg bg-card px-3 py-2 text-sm text-foreground" data-testid={`metrics-teaching-step-${i}`}>
                <span className="ff-mono mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand text-[10px] font-bold text-brand-foreground">{i + 1}</span>{step}
              </li>
            ))}
          </ol>
        </Card>
      )}
      <Tabs defaultValue="manual">
        <TabsList data-testid="metrics-tabs">
          <TabsTrigger value="manual" data-testid="tab-manual">Manual entry</TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-upload">CSV / Excel upload</TabsTrigger>
        </TabsList>

        <TabsContent value="manual">
          <Card className="rounded-xl border bg-card p-5">
            <form onSubmit={saveManual}>
              <div className="mb-4 max-w-xs">
                <Label htmlFor="period">Period (month)</Label>
                <MonthPicker value={form.period || ""} onChange={(v) => setField("period", v)} className="mt-1" data-testid="metric-period" />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {METRIC_DEFS.map((d) => (
                  <div key={d.key}>
                    <Label htmlFor={d.key} className="flex items-center gap-1">{d.label} <span className="text-xs font-normal text-muted-foreground">({d.hint})</span></Label>
                    <Input id={d.key} type="number" step="any" value={form[d.key] ?? ""} onChange={(e) => setField(d.key, e.target.value)} className="mt-1" placeholder="-" data-testid={`metric-${d.key}`} />
                  </div>
                ))}
              </div>
              <div className="mt-6 border-t pt-4">
                <div className="mb-2 flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Custom KPIs</Label>
                </div>
                {customDefs.length > 0 && (
                  <div className="mb-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {customDefs.map((d) => (
                      <div key={d.key}>
                        <Label htmlFor={`c_${d.key}`} className="flex items-center gap-1.5">
                          {d.name} <span className="text-xs font-normal text-muted-foreground">({d.unit})</span>
                          <button type="button" onClick={() => removeDef(d.key)} className="text-muted-foreground hover:text-red-600" data-testid={`custom-def-remove-${d.key}`} aria-label={`Remove ${d.name}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </Label>
                        <Input id={`c_${d.key}`} type="number" step="any" value={form[`c_${d.key}`] ?? ""} onChange={(e) => setField(`c_${d.key}`, e.target.value)} className="mt-1" placeholder="-" data-testid={`metric-custom-${d.key}`} />
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Input value={newDef.name} onChange={(e) => setNewDef((f) => ({ ...f, name: e.target.value }))} placeholder="New KPI name (e.g. NPS, Signups)" className="h-9 max-w-[240px]" data-testid="custom-def-name" />
                  <Select value={newDef.unit} onValueChange={(v) => setNewDef((f) => ({ ...f, unit: v }))}>
                    <SelectTrigger className="h-9 w-[130px]" data-testid="custom-def-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="currency">Currency</SelectItem>
                      <SelectItem value="percent">Percent</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={addDef} disabled={!newDef.name.trim()} data-testid="custom-def-add">
                    <Plus className="h-3.5 w-3.5" /> Add KPI
                  </Button>
                </div>
              </div>
              <div className="mt-5">
                <Button type="submit" disabled={saving} className="gap-2" data-testid="metric-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save metrics
                </Button>
              </div>
            </form>
          </Card>
        </TabsContent>

        <TabsContent value="upload">
          <Card className="rounded-xl border bg-card p-5">
            <p className="text-sm text-muted-foreground">Upload a CSV or Excel file with a <code className="rounded bg-secondary px-1">period</code> column (format <b>YYYY-MM</b>) plus any metric columns. Existing periods are updated.</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onUpload} className="hidden" data-testid="metrics-csv-upload" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2" data-testid="upload-trigger">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Choose file
              </Button>
              <Button variant="outline" onClick={downloadTemplate} className="gap-2" data-testid="download-template">
                <Download className="h-4 w-4" /> Download template
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {customDefs.length > 0 && metrics.length > 0 && (
        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="h-display text-lg font-semibold text-foreground">Custom KPI trend</h2>
            <Select value={chartKpi || customDefs[0]?.key} onValueChange={setChartKpi}>
              <SelectTrigger className="h-9 w-[200px]" data-testid="custom-chart-select"><SelectValue /></SelectTrigger>
              <SelectContent>{customDefs.map((d) => <SelectItem key={d.key} value={d.key}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Card className="min-h-[220px] rounded-xl border bg-card p-5" data-testid="custom-kpi-chart">
            <TrendChart
              data={metrics.map((m) => ({ ...m, __custom: customValue(m, chartKpi || customDefs[0]?.key) }))}
              dataKey="__custom"
              type={(customDefs.find((d) => d.key === (chartKpi || customDefs[0]?.key))?.unit) === "currency" ? "currency" : (customDefs.find((d) => d.key === (chartKpi || customDefs[0]?.key))?.unit) === "percent" ? "percent" : "number"}
              color="hsl(var(--chart-4))"
            />
          </Card>
        </div>
      )}

      <div className="mt-8">
        <h2 className="mb-3 h-display text-lg font-semibold text-foreground">Metric history</h2>
        <Card className="overflow-hidden rounded-xl border bg-card">
          <div className="overflow-x-auto">
            <Table data-testid="metrics-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  {METRIC_DEFS.map((d) => <TableHead key={d.key} className="whitespace-nowrap text-right">{d.label}</TableHead>)}
                  {customDefs.map((d) => <TableHead key={d.key} className="whitespace-nowrap text-right text-primary">{d.name}</TableHead>)}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={METRIC_DEFS.length + customDefs.length + 2} className="py-8 text-center text-muted-foreground">Loading…</TableCell></TableRow>
                ) : metrics.length === 0 ? (
                  <TableRow><TableCell colSpan={METRIC_DEFS.length + customDefs.length + 2} className="py-8 text-center text-muted-foreground">No metrics yet.</TableCell></TableRow>
                ) : (
                  [...metrics].reverse().map((m) => (
                    <TableRow key={m.id} data-testid={`metric-row-${m.period}`}>
                      <TableCell className="font-medium">{formatPeriodLabel(m.period)}</TableCell>
                      {METRIC_DEFS.map((d) => <TableCell key={d.key} className="text-right tabular-nums">{formatMetric(d.type, m[d.key])}</TableCell>)}
                      {customDefs.map((d) => <TableCell key={d.key} className="text-right tabular-nums" data-testid={`custom-cell-${m.period}-${d.key}`}>{formatMetric(d.unit, customValue(m, d.key))}</TableCell>)}
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => remove(m.id)} data-testid={`metric-delete-${m.period}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
